import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { applySEO } from '@/components/feature/SEOHead';
import { featuredNovel, hotNovels, categories, newUpdates, recommendedNovels } from '@/mocks/novelData';

export default function Home() {
  useEffect(() => {
    applySEO({
      title: '公厕by竹巷笔趣阁最新章节-免费在线阅读 | 笔趣阁',
      description: '公厕by竹巷最新章节免费阅读，笔趣阁同步更新，全文无删减在线收藏。追读公厕by竹巷全本小说，第一时间获取最新章节更新，支持手机电脑多端阅读，书友必备追书平台。',
      keywords: '公厕by竹巷,公厕笔趣阁,公厕最新章节,竹巷小说,公厕全文阅读,笔趣阁最新章节,公厕免费阅读',
      canonicalUrl: typeof window !== 'undefined' ? window.location.origin + (window as any).__BASE_PATH__ || '' : '',
      ogImage: featuredNovel.coverImage,
      schema: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: '笔趣阁',
        url: typeof window !== 'undefined' ? window.location.origin + ((window as any).__BASE_PATH__ || '') : '',
        description: '公厕by竹巷最新章节免费阅读，笔趣阁同步更新，全文无删减在线收藏。追读公厕by竹巷全本小说，第一时间获取最新章节更新。',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: typeof window !== 'undefined' ? window.location.origin + ((window as any).__BASE_PATH__ || '') + '/search?q={search_term_string}' : '/search?q={search_term_string}',
          },
          'query-input': 'required name=search_term_string',
        },
      },
    });
  }, []);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20dark%20atmospheric%20city%20alley%20at%20night%20with%20warm%20amber%20streetlight%20reflections%20on%20wet%20pavement%2C%20misty%20moody%20atmosphere%2C%20subtle%20rain%2C%20cinematic%20composition%2C%20minimalist%20urban%20photography%2C%20deep%20shadows%20with%20soft%20light%20patches%2C%20film%20noir%20aesthetic&width=1800&height=900&seq=hero-bg-home-01&orientation=landscape"
              alt="公厕by竹巷 - 都市现实主义小说"
              title="公厕by竹巷 - 笔趣阁热门小说"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden shadow-2xl">
                  <img
                    src={featuredNovel.coverImage}
                    alt="公厕by竹巷封面"
                    title="公厕by竹巷 - 竹巷现实主义力作"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 text-center lg:text-left">
                <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-4">
                  {featuredNovel.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-xs font-medium bg-background-50/20 text-background-50 rounded-full border border-background-50/30 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                  <span className="px-3 py-1 text-xs font-medium bg-accent-500/80 text-background-50 rounded-full whitespace-nowrap">
                    {featuredNovel.status}
                  </span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-3">
                  {featuredNovel.title}
                </h1>
                <p className="text-lg text-background-200 mb-2">
                  <span className="font-medium">{featuredNovel.author}</span> 著
                </p>

                <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-6 text-sm text-background-300">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400"></i>
                    {featuredNovel.rating}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {featuredNovel.readers} 在读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-file-text-line"></i>
                    {featuredNovel.wordCount}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-stack-line"></i>
                    {featuredNovel.totalChapters}章
                  </span>
                </div>

                <p className="text-background-300 text-sm leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
                  {featuredNovel.description}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                  <Link
                    to={`/article/${featuredNovel.latestChapterId}`}
                    className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-center"
                  >
                    立即阅读最新章节
                  </Link>
                  <Link
                    to={`/article/ch-1`}
                    className="px-8 py-3 bg-background-50/15 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/25 border border-background-50/30 transition-colors whitespace-nowrap cursor-pointer text-center"
                  >
                    从头开始阅读
                  </Link>
                </div>

                <div className="mt-6 text-sm text-background-400">
                  最新更新：{featuredNovel.latestChapter} · {featuredNovel.updateTime}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Latest Updates Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  最新章节更新
                </h2>
                <p className="text-foreground-500 text-sm mt-1">第一时间获取作者最新更新内容</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                查看更多 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {newUpdates.map((novel) => (
                <Link
                  key={novel.id}
                  to={`/article/${novel.latestChapterId}`}
                  className="novel-card cursor-pointer group"
                >
                  <div className="flex gap-4">
                    <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden">
                      <img
                        src={novel.coverImage}
                        alt={novel.title}
                        title={`${novel.title} - ${novel.author}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm group-hover:text-primary-600 transition-colors truncate">
                        {novel.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{novel.author}</p>
                      <p className="text-xs text-foreground-600 mt-2 truncate">{novel.latestChapter}</p>
                      <p className="text-xs text-foreground-400 mt-1">{novel.updateTime}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                分类浏览
              </h2>
              <p className="text-foreground-500 text-sm mt-2">八大品类，十万好书，总有一本适合你</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-5 text-center hover:border-primary-300 hover:bg-background-50 transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-50 rounded-full group-hover:bg-primary-100 transition-colors">
                    <i className="ri-book-2-line text-primary-500 text-xl"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm">{cat.name}</h3>
                  <p className="text-xs text-foreground-400 mt-1">{cat.count} 部作品</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Novels Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  热门推荐
                </h2>
                <p className="text-foreground-500 text-sm mt-1">千万读者的选择，口碑保证</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                全部榜单 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
              {hotNovels.map((novel) => (
                <Link
                  key={novel.id}
                  to={`/article/${novel.latestChapterId}`}
                  className="novel-card cursor-pointer group text-center"
                >
                  <div className="w-full aspect-[3/4] rounded-md overflow-hidden mb-3">
                    <img
                      src={novel.coverImage}
                      alt={novel.title}
                      title={`${novel.title} - ${novel.author}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm group-hover:text-primary-600 transition-colors truncate">
                    {novel.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{novel.author}</p>
                  <div className="flex items-center justify-center gap-1 mt-1.5">
                    <i className="ri-star-fill text-amber-400 text-xs"></i>
                    <span className="text-xs text-foreground-600">{novel.rating}</span>
                    <span className="text-xs text-foreground-400 ml-1">{novel.status}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Recommended Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                编辑精选
              </h2>
              <p className="text-foreground-500 text-sm mt-1">为你甄选优质好文</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {recommendedNovels.map((novel) => (
                <Link
                  key={novel.id}
                  to={`/article/${novel.latestChapterId}`}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-5 flex gap-5 cursor-pointer hover:border-primary-300 transition-all group"
                >
                  <div className="w-20 h-28 flex-shrink-0 rounded-md overflow-hidden">
                    <img
                      src={novel.coverImage}
                      alt={novel.title}
                      title={`${novel.title} - ${novel.author}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-xs bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                        {novel.category}
                      </span>
                      <span className="text-xs text-foreground-400">{novel.status}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                      {novel.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{novel.author} · {novel.wordCount}</p>
                    <p className="text-sm text-foreground-600 mt-2 line-clamp-2">{novel.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <span className="text-xs text-foreground-600">{novel.rating}</span>
                      <span className="text-xs text-foreground-400">· {novel.readers} 在读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              随时随地，追读不等待
            </h2>
            <p className="text-foreground-500 mb-8">
              支持PC端、手机端、平板电脑多设备无缝切换阅读，自动保存进度，走到哪看到哪。
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to={`/article/${featuredNovel.latestChapterId}`}
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                开始阅读最新章节
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-secondary-100 text-secondary-800 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}